import styles from './add-product.module.css'
import Page from '../../components/page'
import CustomInput from '../../components/input'
import CustomCascader from '../../components/custom-cascader'
import { Steps, Form, Button, Row, Select, notification, Modal, Divider, Input, Collapse, Space, Checkbox, Table, Tag, InputNumber } from 'antd'
import { useEffect, useState } from 'react'
import { convertStringToEnum } from '../../utils'
import { addProduct, fetchProductCategories, updateProduct } from '../../store/products/actions'
import { useDispatch, useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import { Products } from '../../services/api/products'
import SpecificationInput from '../../components/specification-input'
import { CloseOutlined, PlusOutlined, MinusOutlined } from '@ant-design/icons'
import CustomSearchableSelect from '../../components/custom-searchable-select'
import { convertMeasurementUnit, MeasurementUnits, VolumeUnits, WeightUnits } from '../../utils/units'

const { Step } = Steps
const { Panel } = Collapse

export const specificationTypes = [
	{
		value: 'NUMBER',
		label: 'Number Input'
	},
	{
		value: 'STRING',
		label: 'Text Input'
	},
	{
		value: 'BOOLEAN',
		label: 'Checkbox'
	},
	{
		value: 'WEIGHT',
		label: 'Weight Input'
	},
	{
		value: 'VOLUME',
		label: 'Volume Input'
	},
	{
		value: 'LENGTH',
		label: 'Measurement Input'
	}
]

export const measurementUnits = [
	{
		value: 'mm',
		label: 'Millimetres'
	},
	{
		value: 'cm',
		label: 'Centimetres'
	},
	{
		value: 'm',
		label: 'Metres'
	},
	{
		value: 'in',
		label: 'Inches'
	},
	{
		value: 'ft',
		label: 'Feet'
	},
	{
		value: 'yd',
		label: 'Yards'
	}
]

export const weightUnits = [
	{
		value: 'g',
		label: 'Grams'
	},
	{
		value: 'kg',
		label: 'Kilograms'
	},
	{
		value: 'lbs',
		label: 'Pounds'
	},
	{
		value: 'oz',
		label: 'Ounces'
	},
	{
		value: 'tonne',
		label: 'Tonnes'
	}
]

export const volumeUnits = [
	{
		value: 'm3',
		label: 'Cubic Metres'
	},
	{
		value: 'l',
		label: 'Litres'
	},
	{
		value: 'cc',
		label: 'Cubic Centimetres'
	},
	{
		value: 'cuin',
		label: 'Cubic Inches'
	},
	{
		value: 'cuft',
		label: 'Cubic Feet'
	}
]

export const packageTypes = [
	{
		value: 'CARTON',
		label: 'Carton'
	},
	{
		value: 'BAG',
		label: 'Bag'
	},
	{
		value: 'BOX',
		label: 'Box'
	},
	{
		value: 'CRATE',
		label: 'Crate'
	},
	{
		value: 'SACK',
		label: 'Sack'
	}
]

export const packageUnits = [
	{
		value: 'PIECE',
		label: 'Piece'
	},
	{
		value: 'PACKET',
		label: 'Packet'
	},
	{
		value: 'LITRE',
		label: 'Litre'
	},
	{
		value: 'KILOGRAM',
		label: 'Kilogram'
	}
]

export const ProductTypes = {
	DOMESTIC: 'DOMESTIC',
	EASTWOOD: 'EASTWOOD'
}

export const productionOrderTypes = [
	{ value: 'REGULAR', code: 'RG', label: 'Regular', defaultShelfLife: 120 },
	{ value: 'REWORK', code: 'RW', label: 'Rework', defaultShelfLife: 90 },
	{ value: 'TRIAL', code: 'TR', label: 'Trial', defaultShelfLife: 120 },
	{ value: 'NPI', code: 'NP', label: 'NPI', defaultShelfLife: 120 }
]

export const orderTypeTagStyle = {
	color: '#278EA5',
	background: 'rgba(39, 142, 165, 0.1)',
	border: 'none'
}

const buildDefaultShelfLives = () => productionOrderTypes.map(orderType => ({
	productionOrderType: orderType.value,
	code: orderType.code,
	name: orderType.label,
	shelfLifeDays: orderType.defaultShelfLife
}))

const mergeShelfLives = (serverShelfLives = []) => {
	const shelfLifeByType = {}
	serverShelfLives.forEach(shelfLife => {
		shelfLifeByType[shelfLife.productionOrderType] = shelfLife.shelfLifeDays
	})
	return productionOrderTypes.map(orderType => ({
		productionOrderType: orderType.value,
		code: orderType.code,
		name: orderType.label,
		shelfLifeDays: shelfLifeByType[orderType.value] ?? orderType.defaultShelfLife
	}))
}

const defaultPackage = {
	name: '',
	width: 0,
	widthUnit: MeasurementUnits.M,
	height: 0,
	heightUnit: MeasurementUnits.M,
	length: 0,
	lengthUnit: MeasurementUnits.M,
	volume: 0,
	volumeUnit: VolumeUnits.M3,
	type: packageTypes[0].value,
	size: 1,
	sizeUnit: packageUnits[0].value
}

const AddProductPage = () => {
	const dispatch = useDispatch()
	const router = useRouter()
	const { query, isReady } = router
	const { productId, type: typeValue } = query
	const { categories } = useSelector(state => state.productsReducer)
	const [, setIsFetchingCategories] = useState(false)
	const [currentStep, setCurrentStep] = useState(0)
	const [name, setName] = useState('')
	const [summary, setSummary] = useState('')
	const [brandShortDescription, setBrandShortDescription] = useState('')
	const [selectedCategories, setSelectedCategories] = useState([])
	const [type, setType] = useState(typeValue === ProductTypes.EASTWOOD ? ProductTypes.EASTWOOD : ProductTypes.DOMESTIC)
	const [productType, setProductType] = useState('')
	const [categoryOptions, setCategoryOptions] = useState([])
	const [sku, setSKU] = useState('')
	const [internalId, setInternalId] = useState('')
	const [productNumber, setProductNumber] = useState('')
	const [gtin, setGtin] = useState('')
	const [barcodeVariant, setBarcodeVariant] = useState('')
	const [isWritingProduct, setIsWritingProduct] = useState(false)
	const [isEditMode, setIsEditMode] = useState(false)
	const [productData, setProductData] = useState()
	const [defaultSpecifications, setDefaultSpecifications] = useState([])
	const [newCategory, setNewCategory] = useState('')
	const [selectedSpecificationType, setSelectedSpecificationType] = useState()
	const [selectedSpecifications, setSelectedSpecifications] = useState([])
	const [selectedSpecification, setSelectedSpecification] = useState()
	const [isLoadingSelectedSpecifications, setIsLoadingSelectedSpecifications] = useState(false)
	const [specifications, setSpecifications] = useState([])
	const [specificationKeys, setSpecificationKeys] = useState({})
	const [specificationLabel, setSpecificationLabel] = useState('')
	const [isSpecificationDefault, setIsSpecificationDefault] = useState(false)
	const [isCreatingSpecification, setIsCreatingSpecification] = useState(false)
	const [panelActiveKey, setPanelActiveKey] = useState([])
	const [packages, setPackages] = useState([])
	const [shelfLives, setShelfLives] = useState(buildDefaultShelfLives)

	useEffect(() => {
		getProductCategories()
	}, [])

	useEffect(() => {
		if (isReady) {
			if (productId) {
				getProduct()
			} else {
				fetchDefaultSpecifications()
			}
		}
	}, [isReady])

	useEffect(() => {
		createCategoryOptions()
	}, [categories])

	const getProduct = async () => {
		try {
			const response = await Products.single(productId, { packages: true })
			if (response.data) {
				setIsEditMode(true)
				populateProductData(response.data)
			} else {
				Modal.error({
					title: 'Product Error',
					content: 'Product does not exist or you do not have permission to edit.',
					closable: false,
					okText: 'OK',
					onOk: () => router.push('/')
				})
			}
		} catch (e) {
			console.error(e)
		}
	}

	const getProductCategories = async () => {
		setIsFetchingCategories(true)
		await dispatch(fetchProductCategories())
		setIsFetchingCategories(false)
	}

	const fetchDefaultSpecifications = async () => {
		const response = await Products.fetchSpecifications({ isDefault: true })
		if (response.data) {
			const specifications = []
			const specificationKeys = {}
			response.data.forEach(data => {
				const specification = { ...data }
				setDefaultUnit(specification)
				specifications.push(specification)
				specificationKeys[data.key] = true
			})
			setSpecifications(specifications)
			setSpecificationKeys(specificationKeys)
			setDefaultSpecifications(JSON.parse(JSON.stringify(specifications)))
		}
	}

	const onSpecificationTypeSelected = async (type) => {
		setSelectedSpecifications([])
		setSelectedSpecification(null)
		setSelectedSpecificationType(type)
		setIsLoadingSelectedSpecifications(true)
		const response = await Products.fetchSpecifications({ type })
		if (response.data) {
			const specifications = response.data.map(specification => ({
				data: specification,
				value: specification.key,
				label: specification.label,
				disabled: specificationKeys[specification.key]
			}))
			setSelectedSpecifications(specifications)
		}
		setIsLoadingSelectedSpecifications(false)
	}

	const onSpecificationSelected = value => {
		setSelectedSpecification(value)
	}

	const onAddSpecification = () => {
		const spec = selectedSpecifications.find(spec => spec.value === selectedSpecification)
		if (spec) {
			const specificationData = spec.data
			setDefaultUnit(specificationData)
			const newSpecifications = [...specifications, specificationData]
			setSpecifications(newSpecifications)
			const newSpecificationKeys = { ...specificationKeys, [selectedSpecification]: true }
			setSpecificationKeys(newSpecificationKeys)
		}
		setSelectedSpecifications([])
		setSelectedSpecification(null)
		setSelectedSpecificationType(null)
	}

	const onRemoveSpecification = (index) => {
		const newSpecifications = [...specifications]
		const [removed] = newSpecifications.splice(index, 1)
		const newSelectedSpecifications = [...selectedSpecifications]
		const selectedIndex = selectedSpecifications.findIndex(spec => spec.value === removed.key)
		if (selectedIndex > -1) {
			newSelectedSpecifications[selectedIndex].disabled = false
			setSelectedSpecifications(newSelectedSpecifications)
		}
		const newSpecificationKeys = { ...specificationKeys, [removed.key]: false }
		setSpecificationKeys(newSpecificationKeys)
		setSpecifications(newSpecifications)
	}

	const onSpecificationValueChanged = (index, value) => {
		const newSpecifications = [...specifications]
		newSpecifications[index].value = value !== null && value !== undefined ? `${value}` : ''
		setSpecifications(newSpecifications)
	}

	const onSpecificationUnitChanged = (index, value) => {
		const newSpecifications = [...specifications]
		newSpecifications[index].unit = `${value}`
		setSpecifications(newSpecifications)
	}

	const onCreateSpecification = async () => {
		try {
			setIsCreatingSpecification(true)
			const response = await Products.createSpecification({
				label: specificationLabel,
				type: selectedSpecificationType,
				key: convertStringToEnum(specificationLabel),
				isDefault: isSpecificationDefault
			})
			const specification = response.data
			const newSelectedSpecifications = [...selectedSpecifications, {
				data: specification,
				value: specification.key,
				label: specification.label
			}]
			setSelectedSpecifications(newSelectedSpecifications)
			setSpecificationLabel('')
			setPanelActiveKey(0)
		} catch (e) {
			notification.error({
				message: 'Unable to Create Specification',
				description: e.message,
				placement: 'bottomLeft'
			})
		} finally {
			setIsCreatingSpecification(false)
		}
	}

	const populateProductData = (data) => {
		setName(data.name)
		setSummary(data.summary)
		const { category, subCategory1, subCategory2, subCategory3, subCategory4, subCategory5 } = data
		const categories = [category, subCategory1, subCategory2, subCategory3, subCategory4, subCategory5]
			.filter(category => category)
		setSelectedCategories(categories)
		setProductSpecifications(data.productSpecifications)
		setSKU(data.sku)
		setInternalId(data.internalId)
		setProductData(data)
		setPackages(data.packages)
		setShelfLives(mergeShelfLives(data.productShelfLives))
		setType(data.type)
		setBrandShortDescription(data.brandShortDescription)
		setProductNumber(data.productNumber)
		setGtin(data.gtin)
		setBarcodeVariant(data.barcodeVariant)
		setProductType(data.productType)
	}

	const setDefaultUnit = (spec) => {
		if (!spec.unit) {
			switch (spec.type) {
				case 'LENGTH':
					spec.unit = MeasurementUnits.M
					break
				case 'WEIGHT':
					spec.unit = WeightUnits.KG
					break
				case 'VOLUME':
					spec.unit = VolumeUnits.M3
					break
			}
		}
	}

	const onShelfLifeChange = (productionOrderType, value) => {
		setShelfLives(previousShelfLives => previousShelfLives.map(shelfLife =>
			shelfLife.productionOrderType === productionOrderType
				? { ...shelfLife, shelfLifeDays: value }
				: shelfLife
		))
	}

	const isShelfLifeValid = (shelfLifeDays) => Number.isInteger(shelfLifeDays) && shelfLifeDays > 0

	const shelfLifeColumns = [
		{
			title: 'Production Order Type',
			key: 'productionOrderType',
			render: (_, record) => {
				return (
					<span>
						{record.name}
						<Tag style={{ marginLeft: 12, ...orderTypeTagStyle }}>{record.code}</Tag>
					</span>
				)
			}
		},
		{
			title: 'Shelf-Life (Days)',
			key: 'shelfLifeDays',
			render: (_, record) => {
				return (
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<InputNumber
							min={1}
							step={1}
							precision={0}
							value={record.shelfLifeDays}
							status={isShelfLifeValid(record.shelfLifeDays) ? '' : 'error'}
							onChange={value => onShelfLifeChange(record.productionOrderType, value)}
							style={{ width: 120, background: '#FFFFFF', border: '1px solid #C4CDD6', borderRadius: 8 }}
						/>
						<span style={{ marginLeft: 8, color: '#6E7C91' }}>days</span>
					</div>
				)
			}
		}
	]

	const isForm1Valid = () => {
		if (!name) {
			return false
		} else if (name.trim().length === 0) {
			return false
		}
		if (selectedCategories.length === 0) {
			return false
		} else if (!selectedCategories[0] || selectedCategories[0] === '') {
			return false
		}
		if (!shelfLives.every(shelfLife => isShelfLifeValid(shelfLife.shelfLifeDays))) {
			return false
		}
		return true
	}

	const isForm2Valid = () => {
		const areAllSpecificationsValid = specifications.every(spec => {
			if (!spec.value) {
				return false
			} else if (spec.value.trim().length === 0) {
				return false
			}
			return true
		})
		return areAllSpecificationsValid
	}

	const isForm3Valid = () => {
		if (!sku) {
			return false
		} else if (sku.trim().length === 0) {
			return false
		}

		if (!productNumber) {
			return false
		} else if (productNumber.trim().length === 0) {
			return false
		}

		if (!gtin) {
			return false
		} else if (gtin.trim().length === 0) {
			return false
		}

		if (!barcodeVariant) {
			return false
		} else if (barcodeVariant.trim().length === 0) {
			return false
		}

		return true
	}

	const clearDetails = () => {
		if (isEditMode) {
			setName(productData.name)
			setSummary(productData.summary)
			setBrandShortDescription(productData.brandShortDescription)
			const { category, subCategory1, subCategory2, subCategory3, subCategory4, subCategory5 } = productData
			const categories = [category, subCategory1, subCategory2, subCategory3, subCategory4, subCategory5]
				.filter(category => category)
			setSelectedCategories(categories)
			setShelfLives(mergeShelfLives(productData.productShelfLives))
		} else {
			setName('')
			setSummary('')
			setBrandShortDescription('')
			setSelectedCategories([])
			setShelfLives(buildDefaultShelfLives())
		}
	}

	const clearSpecifications = () => {
		if (isEditMode) {
			setProductSpecifications(productData.productSpecifications)
			setPackages(productData.packages)
		} else {
			const specifications = []
			const specificationKeys = {}
			defaultSpecifications.forEach(data => {
				const specification = { ...data }
				specifications.push(specification)
				specificationKeys[data.key] = true
			})
			setSpecifications(specifications)
			setSpecificationKeys(specificationKeys)
			setPackages([])
		}
	}

	const clearAdditionalInformation = () => {
		if (isEditMode) {
			setSKU(productData.sku)
			setInternalId(productData.internalId)
			setProductNumber(productData.productNumber)
			setGtin(productData.gtin)
			setBarcodeVariant(productData.barcodeVariant)
			setProductType(productData.productType)
		} else {
			setSKU('')
			setInternalId('')
			setProductNumber('')
			setGtin('')
			setBarcodeVariant('')
			setProductType('')
		}
	}

	const setProductSpecifications = (productSpecifications) => {
		const specifications = []
		const specificationKeys = {}
		productSpecifications.forEach(productSpecification => {
			const {
				type,
				label
			} = productSpecification.specification
			const specification = {
				...productSpecification,
				type,
				label,
				key: productSpecification.specificationKey
			}
			specifications.push(specification)
			specificationKeys[productSpecification.key] = true
		})
		setSpecifications(specifications)
		setSpecificationKeys(specificationKeys)
	}

	const onAddProduct = async () => {
		setIsWritingProduct(true)
		try {
			const [
				category,
				subCategory1,
				subCategory2,
				subCategory3,
				subCategory4,
				subCategory5
			] = selectedCategories
			await dispatch(addProduct({
				name,
				summary,
				category,
				subCategory1,
				subCategory2,
				subCategory3,
				subCategory4,
				subCategory5,
				sku,
				internalId,
				specifications: specifications.map(specification => ({
					specificationKey: specification.key,
					value: specification.value,
					unit: specification.unit
				})),
				packages,
				shelfLives: shelfLives.map(shelfLife => ({
					productionOrderType: shelfLife.productionOrderType,
					shelfLifeDays: shelfLife.shelfLifeDays
				})),
				price: 0,
				type: type,
				brandShortDescription,
				productNumber,
				gtin,
				barcodeVariant,
				productType
			}))
			clearDetails()
			clearSpecifications()
			clearAdditionalInformation()
			setCurrentStep(0)
			notification.success({
				message: 'Product Added',
				description: 'New product successfully added!',
				placement: 'bottomLeft'
			})
		} catch (e) {
			notification.error({
				message: 'Unable to Add Product',
				description: e.message,
				placement: 'bottomLeft'
			})
		} finally {
			setIsWritingProduct(false)
		}
	}

	const onUpdateProduct = async () => {
		setIsWritingProduct(true)
		try {
			const [
				category,
				subCategory1,
				subCategory2,
				subCategory3,
				subCategory4,
				subCategory5
			] = selectedCategories
			await dispatch(updateProduct(productId, {
				name,
				summary,
				category,
				subCategory1,
				subCategory2,
				subCategory3,
				subCategory4,
				subCategory5,
				sku,
				internalId,
				specifications: specifications.map(specification => ({
					specificationKey: specification.key,
					value: specification.value,
					unit: specification.unit
				})),
				packages,
				shelfLives: shelfLives.map(shelfLife => ({
					productionOrderType: shelfLife.productionOrderType,
					shelfLifeDays: shelfLife.shelfLifeDays
				})),
				brandShortDescription,
				productNumber,
				gtin,
				barcodeVariant,
				productType
			}))
			notification.success({
				message: 'Product Updated',
				description: 'Product successfully updated!',
				placement: 'bottomLeft'
			})
			router.push(`products/${productId}`)
		} catch (e) {
			notification.error({
				message: 'Unable to Update Product',
				description: e.message,
				placement: 'bottomLeft'
			})
		} finally {
			setIsWritingProduct(false)
		}
	}

	const onCategorySelected = (categories) => {
		if (categories[categories.length - 1] === selectedCategories[selectedCategories.length - 1]) {
			categories.pop()
			setSelectedCategories(categories)
			return
		} else {
			setSelectedCategories(categories)
		}
	}

	const onAddNewCategory = () => {
		let optionsRef = [...categoryOptions]
		const options = optionsRef
		for (const selectedCategory of selectedCategories) {
			const selectedOption = optionsRef.find(categoryOption => categoryOption.value === selectedCategory)
			if (selectedOption) {
				optionsRef = selectedOption.children
			}
		}
		optionsRef.push({
			value: convertStringToEnum(newCategory),
			label: newCategory,
			children: []
		})
		setCategoryOptions(options)
		setNewCategory('')
	}

	const createCategoryOptions = () => {
		const options = []
		const createOptions = (categories, options) => {
			Object.keys(categories).forEach(categoryKey => {
				const option = {
					value: categoryKey,
					label: categories[categoryKey].label,
					children: []
				}
				options.push(option)
				if (categories[categoryKey].subCategories) {
					createOptions(categories[categoryKey].subCategories, option.children)
				}
			})
		}
		createOptions(categories, options)
		setCategoryOptions(options)
	}

	const onPackageNameChanged = (index, value) => {
		const newPackages = [...packages]
		newPackages[index].name = value
		setPackages(newPackages)
	}

	const onPackageTypeSelected = (index, value) => {
		const newPackages = [...packages]
		newPackages[index].type = value
		setPackages(newPackages)
	}

	const onPackageValueChanged = (index, value, dimension) => {
		const newPackages = [...packages]
		newPackages[index][dimension] = value
		calculateVolume(index, value, newPackages[index][`${dimension}Unit`], dimension)
		setPackages(newPackages)
	}

	const onPackageUnitChanged = (index, value, dimension) => {
		const newPackages = [...packages]
		newPackages[index][`${dimension}Unit`] = value
		calculateVolume(index, newPackages[index][dimension], value, dimension)
		setPackages(newPackages)
	}

	const calculateVolume = (index, value, unit, dimension) => {
		const newPackages = [...packages]
		const newPackage = newPackages[index]
		if (dimension === 'width') {
			const width = convertMeasurementUnit(value, unit)
			const height = convertMeasurementUnit(newPackage.height, newPackage.heightUnit)
			const length = convertMeasurementUnit(newPackage.length, newPackage.lengthUnit)
			newPackage.volume = width * height * length
			newPackage.volumeUnit = VolumeUnits.M3
		}
		if (dimension === 'height') {
			const width = convertMeasurementUnit(value, unit)
			const height = convertMeasurementUnit(newPackage.width, newPackage.widthUnit)
			const length = convertMeasurementUnit(newPackage.length, newPackage.lengthUnit)
			newPackage.volume = width * height * length
			newPackage.volumeUnit = VolumeUnits.M3
		}
		if (dimension === 'length') {
			const width = convertMeasurementUnit(value, unit)
			const height = convertMeasurementUnit(newPackage.width, newPackage.widthUnit)
			const length = convertMeasurementUnit(newPackage.height, newPackage.heightUnit)
			newPackage.volume = width * height * length
			newPackage.volumeUnit = VolumeUnits.M3
		}
		setPackages(newPackages)
	}

	const onPackageAdded = () => {
		const newPackages = [...packages]
		setPackages([...newPackages, { ...defaultPackage }])
	}

	const onPackageRemoved = index => {
		const newPackages = [...packages]
		newPackages.splice(index, 1)
		setPackages(newPackages)
	}

	function dropdownRender (menus) {
		return (
			<div>
				{menus}
				<Divider style={{ margin: 0 }} />
				<div className={styles.addCategory}>
					<Input
						value={newCategory}
						onChange={e => setNewCategory(e.target.value)}
						style={{ marginLeft: 'auto', width: '150px' }}
						placeholder='Category Name'
					/>
					<Button
						disabled={!newCategory}
						onClick={onAddNewCategory}
						type='primary'
						style={{ marginLeft: 12 }}
					>
						Add Category
					</Button>
				</div>
			</div>
		)
	}
	return (
		<Page parentTitle='Products' childTitle={isEditMode ? 'Edit Product' : 'Add Product'} parentLink='/products'
			titleComponent={() => {
				return (
					<div className={styles.title}>{`${isEditMode ? 'Edit' : 'Add'} Product ${currentStep + 1}/3`}</div>
				)
			}}
		>
			<div className={styles.container}>
				<Steps current={currentStep} className={styles.progressContainer}>
					<Step title='Details' />
					<Step title='Specifications' />
					<Step title='Additional Information' />
				</Steps>
				{
					currentStep === 0 ?
						<Form
							className={styles.formContainer}
						>
							<Form.Item>
								<CustomInput
									onChange={e => setName(e.target.value)}
									value={name}
									title='Product Name'
								/>
							</Form.Item>
							{
								type === ProductTypes.EASTWOOD ?
									<Form.Item>
										<CustomInput
											onChange={e => {
												setBrandShortDescription(e.target.value)
											}}
											type='textarea'
											value={brandShortDescription}
											optional={true}
											title='Brand Short Description'
										/>
									</Form.Item> :
									<Form.Item>
										<CustomInput
											onChange={e => {
												setSummary(e.target.value)
											}}
											type='textarea'
											value={summary}
											optional={true}
											title='Product Summary'
										/>
									</Form.Item>
							}
							<Form.Item>
								<div className={styles.smallTitle}>
									Product Category
								</div>
								<CustomCascader
									changeOnSelect={true}
									style={{ marginTop: 12 }}
									allowClear={true}
									placeholder='Select a Category'
									options={categoryOptions}
									value={selectedCategories}
									onChange={onCategorySelected}
									dropdownRender={dropdownRender}
								/>
							</Form.Item>
							<Form.Item>
								<div className={styles.smallTitle} style={{ marginBottom: 4, fontWeight: 'bold' }}>
									Shelf-Life <span style={{ color: '#EC4C47', marginLeft: 4 }}>*</span>
								</div>
								<div className={styles.smallTitle} style={{ marginBottom: 12, whiteSpace: 'nowrap' }}>
									Enter the shelf-life in days for each Production Order Type. All fields are mandatory.
								</div>
								<div style={{ border: '1px solid #E3E8EF', borderRadius: 8, overflow: 'hidden' }}>
									<Table
										columns={shelfLifeColumns}
										dataSource={shelfLives}
										rowKey='productionOrderType'
										pagination={false}
										size='small'
									/>
								</div>
							</Form.Item>
							<Form.Item>
								<Row className={styles.buttonContainer}>
									<Button
										onClick={clearDetails}
										style={{ width: 98, margin: '0 6px' }}
										shape='round'
										type='default'
									>
										{isEditMode ? 'Reset' : 'Clear'}
									</Button>
									<Button
										disabled={!isForm1Valid()}
										style={{ width: 98, margin: '0 6px' }}
										shape='round'
										type='primary'
										onClick={() => setCurrentStep(1)}
									>
										Continue
									</Button>
								</Row>
							</Form.Item>
						</Form> : null
				}
				{
					currentStep === 1 ?
						<Form
							className={styles.formContainer}
						>
							<Form.Item>
								<h4 style={{ fontWeight: 'bold', color: '#6E7C91' }}>Item Specifications</h4>
								<div style={{ display: 'flex', alignItems: 'center' }}>
									<Collapse
										onChange={setPanelActiveKey}
										activeKey={panelActiveKey}
										style={{ width: '100%' }}
										accordion
									>
										<Panel header='Add Specification'>
											<Space direction='vertical' style={{ width: '100%' }} size='middle'>
												<div>
													<h5 style={{ fontWeight: 'bold' }}>Type</h5>
													<Select
														value={selectedSpecificationType}
														onChange={onSpecificationTypeSelected}
														options={specificationTypes}
														placeholder='Specification Type'
													/>
												</div>
												<div>
													<h5 style={{ fontWeight: 'bold' }}>Specifications</h5>
													<Select
														loading={isLoadingSelectedSpecifications}
														value={selectedSpecification}
														onChange={onSpecificationSelected}
														options={selectedSpecifications}
														placeholder='Specifications'
													/>
												</div>
												<div style={{ display: 'flex' }}>
													<Button
														type='primary'
														disabled={!selectedSpecification}
														style={{ marginLeft:'auto' }}
														onClick={onAddSpecification}
													>
													Add
													</Button>
												</div>
											</Space>
										</Panel>
										<Panel header='Create Specification'>
											<Space direction='vertical' style={{ width: '100%' }} size='middle'>
												<div>
													<h5 style={{ fontWeight: 'bold' }}>Type</h5>
													<Select
														value={selectedSpecificationType}
														onChange={onSpecificationTypeSelected}
														options={specificationTypes}
														placeholder='Specification Type'
													/>
												</div>
												<div>
													<span style={{ display: 'flex' }}><span style={{ color: '#ff4d4f', marginRight: 12, fontSize: 14 }}>*</span><h5 style={{ fontWeight: 'bold' }}>Label </h5></span>
													<Input
														maxLength={150}
														placeholder='Specification Label'
														value={specificationLabel}
														onChange={e => setSpecificationLabel(e.target.value)}
													/>
												</div>
												{
													selectedSpecificationType &&
													<div>
														<h5 style={{ fontWeight: 'bold' }}>Preview</h5>
														<SpecificationInput
															specification={{
																type: selectedSpecificationType,
																label: specificationLabel
															}}
														/>
													</div>
												}
												<Checkbox
													checked={isSpecificationDefault}
													onChange={e => setIsSpecificationDefault(e.target.checked)}
												>
													Set as default
												</Checkbox>
												<div style={{ display: 'flex' }}>
													<Button
														type='primary'
														disabled={!selectedSpecificationType || !specificationLabel}
														style={{ marginLeft:'auto' }}
														onClick={onCreateSpecification}
														loading={isCreatingSpecification}
													>
														Create
													</Button>
												</div>
											</Space>
										</Panel>
									</Collapse>
								</div>
							</Form.Item>
							{
								specifications.length > 0 &&
								<Form.Item>
									<Space direction='vertical' style={{ width: '100%' }} size='middle'>
										{
											specifications.map((specification, index) => {
												return (
													<div
														key={specification.key}
														style={{ display: 'flex', alignItems: 'center' }}
													>
														<SpecificationInput
															specification={specification}
															onChange={value => onSpecificationValueChanged(index, value)}
															onUnitChange={value => onSpecificationUnitChanged(index, value)}
														/>
														<CloseOutlined
															onClick={() => onRemoveSpecification(index)}
															style={{ marginLeft: 12, cursor: 'pointer' }}
														/>
													</div>
												)
											})
										}
									</Space>
								</Form.Item>
							}
							<Form.Item>
								<span style={{ display: 'flex', alignItems: 'center' }}>
									<h4 style={{ fontWeight: 'bold', color: '#6E7C91' }}>Package Specifications</h4><div className={styles.optional}>(Optional)</div>
								</span>
								{
									packages.map((packageData, index) => {
										return (
											<div key={index}>
												<div style={{ display: 'flex', margin: '16px 0' }}>
													<MinusOutlined
														onClick={() => onPackageRemoved(index)}
														className={styles.minusButton}
													/>
												</div>
												<CustomInput
													onChange={e => onPackageNameChanged(index, e.target.value)}
													value={packageData.name}
													title='Name'
													optional={true}
													style={{ marginBottom: 16 }}
												/>
												<CustomSearchableSelect
													title='Type'
													defaultOptions={packageTypes}
													labelIndex='label'
													valueIndex='value'
													placeholder='Select Package Type'
													value={packageData.type}
													onChange={value => onPackageTypeSelected(index, value)}
													labelInValue={false}
													unclipOptions={true}
													showSearch={false}
												/>
												<Space direction='vertical' style={{ width: '100%', marginTop: 16 }} size='middle'>
													<SpecificationInput
														specification={{
															type: 'LENGTH',
															value: packageData.width,
															unit: packageData.widthUnit,
															label: 'Width'
														}}
														onChange={value => onPackageValueChanged(index, value, 'width')}
														onUnitChange={value => onPackageUnitChanged(index, value, 'width')}
													/>
													<SpecificationInput
														specification={{
															type: 'LENGTH',
															value: packageData.height,
															unit: packageData.heightUnit,
															label: 'Height'
														}}
														onChange={value => onPackageValueChanged(index, value, 'height')}
														onUnitChange={value => onPackageUnitChanged(index, value, 'height')}
													/>
													<SpecificationInput
														specification={{
															type: 'LENGTH',
															value: packageData.length,
															unit: packageData.lengthUnit,
															label: 'Length'
														}}
														onChange={value => onPackageValueChanged(index, value, 'length')}
														onUnitChange={value => onPackageUnitChanged(index, value, 'length')}
													/>
													<SpecificationInput
														specification={{
															type: 'VOLUME',
															value: packageData.volume,
															unit: packageData.volumeUnit,
															label: 'Volume'
														}}
														onChange={value => onPackageValueChanged(index, value, 'volume')}
														onUnitChange={value => onPackageUnitChanged(index, value, 'volume')}
													/>
													<SpecificationInput
														specification={{
															type: 'CUSTOM_NUMBER',
															value: packageData.size,
															unit: packageData.sizeUnit,
															label: 'Package Size'
														}}
														options={packageUnits}
														min={1}
														onChange={value => {
															const formattedValue = Math.trunc(value)
															onPackageValueChanged(index, formattedValue, 'size')
														}}
														onUnitChange={value => onPackageUnitChanged(index, value, 'size')}
													/>
												</Space>
											</div>
										)
									})
								}
								<div style={{ display: 'flex' }}>
									<div
										onClick={onPackageAdded}
										style={{ marginLeft: 'auto', marginTop: packages.length === 0 ? 0 : 24 }}
										className={styles.addButton}
									>
										<PlusOutlined />
										{
											packages.length === 0 ?
												<span>Add packaging</span> :
												<span>Add another packaging</span>
										}
									</div>
								</div>
							</Form.Item>
							<Form.Item>
								<Row className={styles.buttonContainer}>
									<Button
										onClick={clearSpecifications}
										style={{ width: 98, margin: '0 6px' }}
										shape='round'
										type='default'
									>
										{isEditMode ? 'Reset' : 'Clear'}
									</Button>
									<Button
										style={{ width: 98, margin: '0 6px' }}
										shape='round'
										type='default'
										onClick={() => setCurrentStep(0)}
									>
										Previous
									</Button>
									<Button
										style={{ width: 98, margin: '0 6px' }}
										shape='round'
										type='primary'
										disabled={!isForm2Valid()}
										onClick={() => setCurrentStep(2)}
									>
										Continue
									</Button>
								</Row>
							</Form.Item>
						</Form> : null
				}
				{
					currentStep === 2 ?
						<Form
							className={styles.formContainer}
						>
							<Form.Item>
								<CustomInput
									onChange={e => {
										setSKU(e.target.value.toUpperCase())
										setInternalId(e.target.value.toUpperCase())
									}}
									type='text'
									value={sku}
									optional={type !== ProductTypes.EASTWOOD}
									tooltip='Stock Keeping Unit number.'
									title='SKU'
								/>
							</Form.Item>
							{
								type === ProductTypes.EASTWOOD &&
								<>
                                	<Form.Item>
										<CustomInput
											onChange={e => {
												setProductType(e.target.value.toUpperCase())
											}}
											type='text'
											value={productType}
											title='Product Type'
										/>
									</Form.Item>
									<Form.Item>
										<CustomInput
											onChange={e => {
												setProductNumber(e.target.value.toUpperCase())
											}}
											type='text'
											value={productNumber}
											title='Product Number'
										/>
									</Form.Item>
									<Form.Item>
										<CustomInput
											onChange={e => {
												setGtin(e.target.value.toUpperCase())
											}}
											type='text'
											value={gtin}
											title='GTIN'
										/>
									</Form.Item>
									<Form.Item>
										<CustomInput
											onChange={e => {
												setBarcodeVariant(e.target.value.toUpperCase())
											}}
											type='text'
											value={barcodeVariant}
											title='Barcode Variant'
										/>
									</Form.Item>
								</>
							}
							<Form.Item>
								<Row className={styles.buttonContainer}>
									<Button
										onClick={clearAdditionalInformation}
										style={{ width: 98, margin: '0 6px' }}
										shape='round'
										type='default'
									>
										{isEditMode ? 'Reset' : 'Clear'}
									</Button>
									<Button
										style={{ width: 98, margin: '0 6px' }}
										shape='round'
										type='default'
										onClick={() => setCurrentStep(1)}
									>
										Previous
									</Button>
									<Button
										loading={isWritingProduct}
										style={{ margin: '0 6px' }}
										shape='round'
										type='primary'
										disabled={type === ProductTypes.EASTWOOD ? !isForm3Valid() : false}
										onClick={isEditMode ? onUpdateProduct : onAddProduct}
									>
										{isEditMode ? 'Update Product' : 'Create Product'}
									</Button>
								</Row>
							</Form.Item>
						</Form> : null
				}
			</div>
		</Page>
	)
}

export default AddProductPage
